import { FunctionComponent } from "react";
import { faSitemap, faUserTie } from "@fortawesome/pro-regular-svg-icons";

import { Checkbox } from "@/components/Checkbox/Checkbox";
import { Tooltip } from "@/components/Tooltip/Tooltip";
import { Icon } from "@/components";
import { classNames } from "@/utils/classNames";
import { IPersonSummary } from "@/pages/Private/redux/search/search.schema";
import { JobTitle } from "@/pages/Private/pages/List/components/CampaignOverview/components/Lead/components/JobTitle";
import { OrgName } from "@/pages/Private/pages/List/components/CampaignOverview/components/Lead/components/OrgName";
import { OrgCategory } from "@/pages/Private/pages/List/components/CampaignOverview/components/Lead/components/OrgCategory";

import { formatDepartmentLabel, formatSeniorityLabel } from "./utils";
import { companyEnrichPersonToListPerson } from "./companyEnrichToListPerson";
import { LinkedInPersonChannels } from "./LinkedInPersonChannels";

interface LinkedInPersonCardProps {
	person: IPersonSummary;
	isSelected: boolean;
	failedImages: Set<string>;
	onToggle: () => void;
	onImageError: (url: string) => void;
}

/**
 * Renders a company-enrich (LinkedIn) person in the same layout as the real lead
 * card by mapping it to a `ListPerson` and reusing Lead's presentational
 * sub-components (OrgName, JobTitle, …). Cells the external person has no data for
 * render blank — the same skeleton as a real lead. Selection stays card-level
 * (own checkbox + `onToggle`); no campaign/score/mutation affordances are rendered.
 */
export const LinkedInPersonCard: FunctionComponent<LinkedInPersonCardProps> = ({
	person,
	isSelected,
	failedImages,
	onToggle,
	onImageError,
}) => {
	const row = companyEnrichPersonToListPerson(person);

	// Same gate as `hasDisplayableCompany`: only render people that resolve to a company.
	if (!row.prospect?.organization) {
		return null;
	}

	// The cell shows the real job title (from the primary experience); the
	// LinkedIn headline (`person.position`) is only offered on hover (PREC-3669).
	const jobTitle = row.prospect.jobTitleToSeniority?.jobTitle;
	const headlineTooltip = person.position && person.position !== jobTitle ? person.position : null;

	const avatarUrl =
		person.image_url && !failedImages.has(person.image_url) ? person.image_url : null;

	return (
		<div className="lead-item flex flex-row gap-2">
			<div
				className={classNames(
					"w-full flex flex-col gap-2 border box-content cursor-pointer relative overflow-hidden p-2",
					isSelected
						? "!border-blue-action bg-brand-white"
						: "border-border-light bg-white hover:bg-brand-white/50 hover:border-blue-action/50"
				)}
				onClick={onToggle}
			>
				<div className="flex flex-row gap-2 w-full">
					<div className="flex justify-center items-center">
						<Checkbox className="w-fit!" isChecked={isSelected} />
					</div>

					<div className="flex flex-row gap-2 grow max-w-[calc(100%-28px)]">
						<div className="flex flex-col gap-2 justify-start">
							{avatarUrl ? (
								<img
									alt={person.name ?? ""}
									className="w-6 h-6 rounded-full object-cover shrink-0"
									src={avatarUrl}
									onError={() => onImageError(person.image_url!)}
								/>
							) : (
								<div className="w-6 h-6 rounded-full bg-blue-action flex items-center justify-center text-white text-xs font-bold shrink-0">
									{person.name?.charAt(0)?.toUpperCase() || "?"}
								</div>
							)}
						</div>

						<div className="flex flex-col gap-1 w-[calc(100%-32px)]">
							<div className="flex flex-row gap-2 w-full max-w-full">
								<div className="flex flex-col gap-1 w-[calc(50%-4px)] max-w-1/2">
									<div className="flex flex-row gap-1 items-center w-full">
										<Tooltip className="max-w-[calc(100%-48px)]" content={person.name}>
											<span className="inline-block font-semibold text-sm max-w-full text-ellipsis text-left overflow-hidden whitespace-nowrap">
												{person.name ?? "No name"}
											</span>
										</Tooltip>
										<div className="flex flex-row gap-0 items-center justify-end shrink-0">
											<LinkedInPersonChannels person={person} />
										</div>
									</div>
								</div>
								<div className="flex flex-col gap-1 w-[calc(50%-4px)] max-w-1/2">
									<OrgName row={row} hideLinkedin />
								</div>
							</div>

							<div className="flex flex-row gap-2 w-full max-w-full">
								<div className="flex flex-col gap-1 w-[calc(50%-4px)] max-w-1/2">
									<JobTitle row={row} tooltipContent={headlineTooltip} />
								</div>
								<div className="flex flex-col gap-1 w-[calc(50%-4px)] max-w-1/2">
									<OrgCategory row={row} />
								</div>
							</div>

							{/* Seniority and department rendered like Lead's language/location cells. */}
							{(person.seniority || person.department) && (
								<div className="flex flex-row gap-2 w-full max-w-full">
									<div className="flex flex-col gap-1 w-[calc(50%-4px)] max-w-1/2 min-w-0">
										{person.seniority && (
											<Tooltip
												className="min-w-0 overflow-hidden"
												content={formatSeniorityLabel(person.seniority)}
											>
												<div className="flex flex-row items-center gap-1.5 min-w-0 overflow-hidden text-sm text-left">
													<Icon className="text-gray-500 w-3.5 h-3.5 shrink-0" icon={faUserTie} />
													<span className="truncate text-status-info">
														{formatSeniorityLabel(person.seniority)}
													</span>
												</div>
											</Tooltip>
										)}
									</div>
									<div className="flex flex-col gap-1 w-[calc(50%-4px)] max-w-1/2 min-w-0">
										{person.department && (
											<Tooltip
												className="min-w-0 overflow-hidden"
												content={formatDepartmentLabel(person.department)}
											>
												<div className="flex flex-row items-center gap-1.5 min-w-0 overflow-hidden text-sm text-left">
													<Icon className="text-gray-500 w-3.5 h-3.5 shrink-0" icon={faSitemap} />
													<span className="truncate text-status-info">
														{formatDepartmentLabel(person.department)}
													</span>
												</div>
											</Tooltip>
										)}
									</div>
								</div>
							)}
						</div>
					</div>
				</div>
			</div>
		</div>
	);
};
